import React, { useState, useEffect } from 'react';
import { Form, ListGroup, Badge } from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';

function SiteSelector({
    tenantId,
    selectedIds = [],
    onChange,
    multiple = true,
    disabled = false,
    existingSiteIds = [], // IDs of sites already linked to this customer
    onSitesLoaded = null // Callback when sites are loaded, passes array of {id, name}
}) {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (tenantId) {
            fetchSites();
        } else {
            setSites([]);
            setLoading(false);
        }
    }, [tenantId]);

    const fetchSites = async () => {
        setLoading(true);

        try {
            // Use tenantId as string (UUID) for the API call - no conversion needed
            const url = tenantId
                ? `${API_BASE_URL}/sites?tenant_id=${tenantId}`
                : `${API_BASE_URL}/sites`;

            const response = await fetchWithAuth(url);

            if (response.ok) {
                const data = await response.json();
                // Keep original field names for API compatibility, but also store site_id for API calls
                const transformedSites = data.map(s => ({
                    siteId: s.site_id,
                    site_id: s.site_id, // Keep original for API calls
                    siteName: s.name,
                    tenantId: s.tenant_id,
                    tenant_name: s.tenant_name,
                    status: s.status
                }));
                setSites(transformedSites);
                
                // Notify parent component that sites are loaded (for getting site names)
                if (onSitesLoaded) {
                    const siteInfo = data.map(s => ({
                        id: s.site_id,
                        name: s.name
                    }));
                    onSitesLoaded(siteInfo);
                }
            } else {
                setError('Failed to fetch sites');
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
            setError('Error fetching sites');
        } finally {
            setLoading(false);
        }
    };

    const handleSiteToggle = (siteId) => {
        if (disabled) return;
        
        let newSelectedIds;
        if (multiple) {
            if (selectedIds.includes(siteId)) {
                newSelectedIds = selectedIds.filter(id => id !== siteId);
            } else {
                newSelectedIds = [...selectedIds, siteId];
            }
        } else {
            newSelectedIds = [siteId];
        }
        onChange(newSelectedIds);
    };

    if (loading) {
        return <LoadingSpinner size="sm" message="Loading sites..." />;
    }

    if (!tenantId) {
        return <div className="text-muted">Please select a tenant first</div>;
    }

    if (error) {
        return <div className="text-danger">{error}</div>;
    }

    if (sites.length === 0) {
        return <div className="text-muted">No sites available for this tenant</div>;
    }

    return (
        <div className="site-selector">
            <Form.Group>
                <Form.Label className="font-weight-bold">
                    {multiple ? 'Select Sites' : 'Select Site'}
                </Form.Label>
                <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {sites.map(site => {
                        const isSelected = selectedIds.includes(site.siteId);
                        // Check if this site is already linked to the customer
                        const isExisting = existingSiteIds.some(id =>
                            String(id) === String(site.siteId) ||
                            String(id) === String(site.site_id)
                        );
                        const isDisabled = disabled || isExisting;
                        
                        return (
                            <ListGroup.Item
                                key={site.siteId}
                                action
                                onClick={() => !isDisabled && handleSiteToggle(site.siteId)}
                                className="d-flex justify-content-between align-items-center"
                                style={{
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.5 : 1,
                                    backgroundColor: isExisting ? '#f8f9fa' : undefined
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <Form.Check
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => !isDisabled && handleSiteToggle(site.siteId)}
                                        disabled={isDisabled}
                                        style={{ marginRight: ".4rem" }}
                                        className="mr-2"
                                    />
                                    <span>
                                        {site.siteName}
                                        {isExisting && <span style={{ marginLeft: ".4rem" }} className="text-muted ml-1">(already linked)</span>}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center">
                                    {site.status && (
                                        <Badge
                                            variant={site.status === 'active' ? 'success' : 'secondary'}
                                            className="mr-2"
                                        >
                                            {site.status}
                                        </Badge>
                                    )}
                                    {isSelected && (
                                        <FaCheck className="text-success" />
                                    )}
                                </div>
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
                
                {selectedIds.length > 0 && (
                    <div className="mt-2">
                        <small className="text-muted">
                            {selectedIds.length} site(s) selected
                        </small>
                    </div>
                )}
            </Form.Group>
        </div>
    );
}

export default SiteSelector;